import React, { useMemo, useState } from "react";
import { useNavigate, useParams, useRouteLoaderData } from "react-router";
import { useTheme, useMediaQuery } from "@mui/material";
import { CalibrationDialog } from "./dialogs/CalibrationDialog";
import { CalibrationStartedDialog } from "./dialogs/CalibrationStartedDialog";
import { CalibrationRestoredSnackbar } from "./CalibrationRestoredSnackbar";
import { Box } from "@mui/material";
import { CalibrationData, FieldsData } from "../../../../../../../API/fields";
import { CalibrationItem, MobileStep } from "./Calibration.types";
import { Products } from "../../../components/ResultSet/ResultSetSelect";
import { CalibrationMap } from "./map/CalibrationMap";
import { YieldCard } from "./map/YieldCard";
import { useCalibration } from "./useCalibration";
import { findWarnableEdgePlots } from "./utils/edge-plots";
import { CalibrationSidebar } from "./sidebar/CalibrationSidebar";
import { MobileProgressCard } from "./mobile/MobileProgressCard";
import { getAverageAppleCountPerTree } from "../../../../../../../Component/Map/Layers/apple-count/apple-count-helper";
import { useTrackedCalibrations } from "../../../../../../../Component/CalibrationBanners/tracked-calibrations";
import { sourceResultSetIdOf } from "../../../../../../../Component/CalibrationBanners/calibration-lineage";

export default function Calibration(): React.ReactNode {
  const navigate = useNavigate();
  const theme = useTheme();

  const { geojson, trees, resultSet } = useRouteLoaderData(
    "calibrate",
  ) as CalibrationData;

  const { resultSetId, customerId, fieldId } = useParams();
  const customer = useRouteLoaderData("customer") as FieldsData | undefined;
  const fieldName =
    customer?.fields.features.find(
      (feature) => feature.properties.id === Number(fieldId),
    )?.properties.name ?? "";
  const trackCalibration = useTrackedCalibrations((state) => state.track);

  const [mobileStep, setMobileStep] = useState<MobileStep>("map");
  const [calibrationStarted, setCalibrationStarted] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { state, derived, actions } = useCalibration({
    geojson,
    resultSet,
    resultSetId,
  });

  const edgePlotIds = useMemo(() => findWarnableEdgePlots(geojson), [geojson]);

  if (resultSet.product_sku !== Products.TS_APPLE) return null;

  const edgeSampleCount = state.calibrationSet.filter((item) =>
    edgePlotIds.has(item.plotId),
  ).length;

  const averageAppleCountPerTree = getAverageAppleCountPerTree(geojson);

  const resultSetPage = `/customer/${customerId}/field/${fieldId}/result-set/${resultSetId}`;

  const handleRunCalibration = async (averageWeight: number) => {
    const outcome = await actions.submitCalibration(averageWeight);
    if (!outcome) return;

    if (outcome.calibrationMeasurementSetId) {
      trackCalibration({
        id: outcome.calibrationMeasurementSetId,
        sourceResultSetId: sourceResultSetIdOf(resultSet),
        resultSetId: resultSetId ?? resultSet.id,
        customerId: customerId ?? "",
        fieldId: fieldId ?? "",
        fieldName,
      });
    }

    if (outcome.kind === "started") {
      setCalibrationStarted(true);
    } else {
      navigate(resultSetPage);
    }
  };

  const acknowledgeCalibrationStarted = () => {
    setCalibrationStarted(false);
    navigate(resultSetPage);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minWidth: 0,
        height: mobileStep === "map" && isMobile ? "86vh" : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          flexGrow: 1,
          overflow: "hidden",
          height:
            mobileStep === "map" && isMobile
              ? {
                  xs: "100vh",
                  md: "auto",
                }
              : {
                  md: "calc(100vh - 64px - var(--calibration-banner-height, 0px))",
                },
        }}
      >
        <CalibrationSidebar
          state={state}
          actions={actions}
          buckets={derived.buckets}
          geojson={geojson}
          runCalibration={handleRunCalibration}
          setMobileStep={setMobileStep}
          mobileStep={mobileStep}
          edgeSampleCount={edgeSampleCount}
        />
        <Box
          sx={{
            flexGrow: 1,
            position: "relative",
            overflow: "hidden",
            display: {
              xs: mobileStep === "map" ? "flex" : "none",
              md: "flex",
            },
            flexDirection: "column",
          }}
        >
          <CalibrationMap
            geojson={geojson}
            trees={trees}
            state={state}
            actions={actions}
            buckets={derived.buckets}
          />
          {state.lastCalibratedDate && (
            <YieldCard
              lastCalibratedDate={state.lastCalibratedDate}
              yieldEstimate={state.yieldEstimate}
              manualCountsCount={state.calibrationSet.length}
              averageAppleCountPerTree={averageAppleCountPerTree}
            />
          )}
          <MobileProgressCard
            setMobileStep={setMobileStep}
            calibrationSet={state.calibrationSet}
            geojson={geojson}
            buckets={derived.buckets}
          />
        </Box>
        {state.selectedFeatureId !== null && (
          <CalibrationDialog
            open={state.selectedFeatureId !== null}
            selectedPlot={state.selectedPlot as CalibrationItem}
            onClose={actions.clearSelection}
            onSave={actions.saveCalibrationItem}
            currentSampleCount={state.calibrationSet.length}
            isNewSample={
              !state.calibrationSet.some(
                (s) => s.plotId === state.selectedFeatureId,
              )
            }
            isEdgePlot={
              state.selectedFeatureId !== null &&
              edgePlotIds.has(state.selectedFeatureId)
            }
          />
        )}
        <CalibrationStartedDialog
          open={calibrationStarted}
          onAcknowledge={acknowledgeCalibrationStarted}
        />
        <CalibrationRestoredSnackbar
          open={state.draftRestored}
          onClose={actions.dismissDraftRestored}
        />
      </Box>
    </Box>
  );
}
